import { inject, observer } from "mobx-react";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { match } from "react-router";
import { theme } from "src/config/theme";
import { Text } from "src/components";
import { SpaceType } from "src/types";
import { getRoutePath, MatchParams, Page } from "src/routes";
import { AuthStore, RootStore, RoutingStore, SpaceStore } from "src/stores";
import styled from "styled-components";

export interface IBreadCrumbsProps {
  routingStore?: RoutingStore;
  rootStore?: RootStore;
  spaceStore?: SpaceStore;
  authStore?: AuthStore;
  onClick?: () => void;
  match?: match<MatchParams>; // This is injected so it has to be called match
}

const Wrapper = styled.div`
  margin-left: 0.5vw;
`;

const Breadcrumbs = styled.ol`
  align-items: center;
  justify-content: left;
  display: flex;
  display: flex;
  flex-direction: row;
  color: ${theme.font.color.black};
`;

const CrumbWrapper = styled.li`
  cursor: pointer;
  font-size: ${theme.font.size.xxsmall};
  font-weight: ${theme.font.weight.normal};
  :last-of-type {
    font-weight: ${theme.font.weight.bold};
  }
  :not(:first-of-type) {
    margin-left: 0.275rem;
    :before {
      content: '';
      opacity: 0.5;
      margin-right: 0.5rem;
      border-style: solid;
      border-width: 0.15em 0.15em 0 0;
      display: inline-block;
      left: 0;
      transform: rotate(45deg);
      position: relative;
      top: 0.6em;
      vertical-align: top;
      height: 0.375em;
      width: 0.375em;
    }
  }
`;

const CrumbLink = styled.span`
  color: inherit;
  text-decoration: none;
  &:hover {
    opacity: 0.8;
  }
`;


const settingsPage = {
  [SpaceType.System] : Page.WorkSpaceSettings, // what is this type?
  [SpaceType.Workspace] : Page.WorkSpaceSettings,
  [SpaceType.Project] : Page.ProjectSettings,
  [SpaceType.Room] : Page.RoomSettings,
};

export const Breadcrumb: FC<IBreadCrumbsProps> = inject(
  "routingStore",
  "rootStore",
  "spaceStore",
  "authStore"
)(
  observer((props: IBreadCrumbsProps): JSX.Element => {
    const { t, i18n } = useTranslation();
    const { routingStore, authStore, spaceStore } = props;
    if(!props.match) return null;
    
    const { path, params, url } = props.match;

    const workspaceId = authStore.getCurWorkspaceID;
    const projectId = authStore.getCurProjectID;
    const roomId = authStore.getCurRoomID;
    const inRoom = spaceStore.inRoom;
    const isSettings = path.includes("/settings");
    const isUserView = path.includes("/user");
    const isWorkspace = path.includes("/workspace");
    const activeItem = props.rootStore.getActiveItem;
    const view = params.view;

    if ((!spaceStore || !spaceStore.getItems) || isUserView || !authStore.isLoggedIn) return null;

    const spacesTree = isWorkspace ?  spaceStore.getSpacePath(workspaceId) : spaceStore.getSpacePath( roomId || projectId || workspaceId );

    if (path === "/") {
      return (
        <Wrapper>
          <Text weight="bold" size="xxsmall">
            {projectId &&
              (spaceStore.getItemByID(workspaceId) || { title: "" }).title}
          </Text>
        </Wrapper>
      );
    }

  

    return (
      <Wrapper>
        <Breadcrumbs>
          {spacesTree.map( space => (
            <CrumbWrapper key={space.id+'x'}>
            <CrumbLink
              onClick={() => {
                if(!authStore.user.canEditSpace(space.id)) return
                routingStore.push(getRoutePath(settingsPage[space.type], {
                    id: space.id,
                    view: "settings"
                  }))
              }}
            >
            {(space.title)}
            </CrumbLink>
          </CrumbWrapper>
          ))}
          {(inRoom && !isSettings && view) && (
            <CrumbWrapper>
              <CrumbLink
                onClick={() => {
                  if(!authStore.user.canEditSpace(roomId)) return
                  routingStore.push(
                    getRoutePath(Page.RoomSettings, {
                      id: roomId,
                      view: view
                    })
                  )
                }}
              >
              {`${t(view + "_plural")} ${params.type ? `/ ` + t(params.type) : ''}`}
              </CrumbLink>
            </CrumbWrapper>
          )}
          {(inRoom && activeItem && view) && (
            <CrumbWrapper>
              <CrumbLink
                onClick={() => {

                  routingStore.push(
                    getRoutePath(Page.Room, {
                      id: activeItem.id,
                      view: props.rootStore.getActiveItemType
                    })
                  )
                }
                }>
                {"#" + activeItem.id}
              </CrumbLink>
            </CrumbWrapper>
          )}
        </Breadcrumbs>
      </Wrapper>
    );
  })
);
